/* eslint-disable max-len */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import ConditionalRender
  from '@avtechsoftware/react-component-library/src/components/common/ConditionalRender/ConditionalRender';
import AppTypes from '@avtechsoftware/ra-javascript-utilities/src/types/AppTypes';
import ChannelGroupType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelGroupType';
import AnalogComputedChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/AnalogComputedChannel/AnalogComputedChannel';
import ChannelKey from '@avtechsoftware/ra-javascript-utilities/src/classes/ChannelKey';
import AltErrorWrapper
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/AltErrorWrapper/AltErrorWrapper';
import FormErrorHandler
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormErrorHandler';
import ChannelType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelType';
import { useDispatch, useSelector } from 'react-redux';
import PushStatusChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/PushStatusChannel/PushStatusChannel';
import BatteryStrengthChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BatteryStrengthChannel/BatteryStrengthChannel';
import ProgressSpinner from '@avtechsoftware/react-component-library/src/components/common/ProgressSpinner';
import ClassUtility from '../../../../utility/ClassUtility';
import {
  selectCompoundSensorSpecList,
  selectRoomAlertDeviceList,
  selectSensorMap,
} from '../../../../redux/slices/deviceList/deviceListSlice';
import {
  fetchSingleDevice,
  selectHiddenChannelGroups,
} from '../../../../redux/slices/singleDeviceSlice/singleDeviceSlice';
import { filterHiddenChannelGroups, parseChannelKey } from '../../../../utility/channelUtils';

function ChannelSelector(props) {
  const {
    channelKey,
    updateChannelKey,
    setIsDeviceSensor,
    errorList,
    isQuickAlert,
  } = props;
  const dispatch = useDispatch();
  const deviceList = useSelector(selectRoomAlertDeviceList);
  const availableDeviceList = isQuickAlert ? deviceList.filter((d) => d.type === 'RAMAX') : deviceList;
  const sensorMap = useSelector(selectSensorMap);
  const compoundSensorSpecList = useSelector(selectCompoundSensorSpecList);
  const hiddenSensors = useSelector(selectHiddenChannelGroups);
  const [isMaxDevice, setIsMaxDevice] = React.useState(false);
  const [loadingDeviceData, setLoadingDeviceData] = React.useState(false);

  const unfilteredChannelGroupList = sensorMap[channelKey.macAddress] || [];
  const mappedHiddenChannelGroups = hiddenSensors.map((hiddenChannelGroup) => parseChannelKey(hiddenChannelGroup));
  const channelGroupFieldMap = {
    channelGroupTypeId: 'channelGroupType',
    channelGroupMacAddress: 'sensorMacAddress',
    channelGroupIndex: 'channelGroupIndex',
  };

  const channelGroupList = filterHiddenChannelGroups(unfilteredChannelGroupList, mappedHiddenChannelGroups, channelGroupFieldMap);
  const chanGroup = channelGroupList.length && channelGroupList.find((sensor) => sensor.channelGroupTypeId === channelKey.channelGroupType && sensor.channelGroupIndex === channelKey.channelGroupIndex);
  let channelList = ChannelService
    .getChannelGroupTypeByValueId(Number(channelKey.channelGroupType))
    .channels.filter(
      (channel) => channel.isAlertable && (channel.appType === AppTypes.UNIVERSAL
              || channel.appType === AppTypes.CLOUD),
    );
  channelList = channelList.filter((channel) => !(channel.channelType === ChannelType.ALARM && !chanGroup.hasAlarmChannel));
  const analogComputedSensor = channelGroupList.find(
    (sensor) => sensor.conversion !== null
          && sensor.channelGroupMacAddress === channelKey.channelGroupMacAddress
          && sensor.channelGroupTypeId === channelKey.channelGroupType
          && sensor.channelGroupIndex === channelKey.channelGroupIndex,
  );

  if (channelKey.channelGroupType === ChannelGroupType.COMPUTED_SENSOR.id) {
    const targetComputedChannel = compoundSensorSpecList.find(
      (computedDetails) => Number(computedDetails.outputChannel.channelGroup.channelGroupIndex)
            === channelKey.channelGroupIndex,
    );
    channelList = targetComputedChannel
      ? channelList.filter(
        (channel) => channel.channelType.cloudCode === targetComputedChannel.outputChannel.type
                && channel.index === Number(targetComputedChannel.outputChannel.channelIndex),
      )
      : [];
  }

  if (channelKey.channelGroupType === ChannelGroupType.BLUETOOTH_LE.id) {
    if (channelKey.channelGroupIndex === 3) {
      channelList = channelList.filter((channel) => channel instanceof PushStatusChannel);
    } else {
      channelList = channelList.filter((channel) => channel instanceof PushStatusChannel || channel instanceof BatteryStrengthChannel);
    }
  }

  const isRamaxDevice = (deviceId) => {
    const device = availableDeviceList.find((d) => d.uuid === deviceId);
    return device && device.type === 'RAMAX';
  };

  const handleDeviceAssignmentChange = async (e) => {
    const { value } = e.target;
    setLoadingDeviceData(true);
    const device = availableDeviceList.find((d) => d.uuid === value);
    await dispatch(fetchSingleDevice({ deviceId: device.id }));
    setLoadingDeviceData(false);
    const updatedChannelKey = ClassUtility.cloneObject(channelKey);
    updatedChannelKey.macAddress = value;
    updatedChannelKey.channelGroupMacAddress = '';
    updatedChannelKey.setChannelGroupType(0);
    updatedChannelKey.setChannelGroupIndex(0);
    updatedChannelKey.setChannelType(0);
    updatedChannelKey.setChannelIndex(0);
    updateChannelKey(updatedChannelKey);
    setIsMaxDevice(isRamaxDevice(value));
  };

  const handleMaxComponentChange = (e) => {
    const { value } = e.target;
    const updatedChannelKey = ClassUtility.cloneObject(channelKey);
    // Only set the channelGroupMacAddress, reset other values
    updatedChannelKey.setChannelGroupMacAddress(value);
    updatedChannelKey.setChannelGroupType(0);
    updatedChannelKey.setChannelGroupIndex(0);
    updatedChannelKey.setChannelType(0);
    updatedChannelKey.setChannelIndex(0);

    updateChannelKey(updatedChannelKey);
  };

  const handleChannelGroupChange = (e) => {
    const { value } = e.target;
    const updatedChannelKey = ClassUtility.cloneObject(channelKey);
    const channelGroupDetails = value.split('_');
    const [channelGroupMacAddress, channelGroupType, channelGroupIndex] = channelGroupDetails;
    setIsDeviceSensor(channelGroupType === '1300');
    updatedChannelKey.setChannelGroupMacAddress(channelGroupMacAddress);
    updatedChannelKey.setChannelGroupType(channelGroupType);
    updatedChannelKey.setChannelGroupIndex(channelGroupIndex);
    updatedChannelKey.setChannelType(0);
    updatedChannelKey.setChannelIndex(0);
    updateChannelKey(updatedChannelKey);
  };

  const handleMaxSensorChange = (e) => {
    const { value } = e.target;
    const updatedChannelKey = ClassUtility.cloneObject(channelKey);
    const channelGroupDetails = value.split('_');
    const [channelGroupType, channelGroupIndex] = channelGroupDetails;
    setIsDeviceSensor(channelGroupType === '1300');
    // Keep the existing channelGroupMacAddress from the component selection
    updatedChannelKey.setChannelGroupType(channelGroupType);
    updatedChannelKey.setChannelGroupIndex(channelGroupIndex);
    updatedChannelKey.setChannelType(0);
    updatedChannelKey.setChannelIndex(0);

    updateChannelKey(updatedChannelKey);
  };

  const maxComponentList = channelGroupList
    .filter((group) => group.channelGroupTypeId === 205);

  const getMaxComponentSensorList = () => {
    const isBaseStationComponent = channelKey.macAddress === channelKey.channelGroupMacAddress;
    if (!isBaseStationComponent) {
      return channelGroupList.filter((sensor) => sensor.channelGroupMacAddress === channelKey.channelGroupMacAddress);
    }
    return channelGroupList.filter((sensor) => {
      const isTemperatureSensor = sensor.channelGroupTypeId === ChannelGroupType.TEMPERATURE.id;
      const isDeviceSensor = sensor.channelGroupTypeId === ChannelGroupType.DEVICE.id;
      const hasMatchingMacAddress = sensor.channelGroupMacAddress === channelKey.macAddress;

      return hasMatchingMacAddress && (isTemperatureSensor || isDeviceSensor);
    });
  };

  const handleChannelChange = (e) => {
    const { value } = e.target;
    const updatedChannelKey = ClassUtility.cloneObject(channelKey);
    const channelDetails = value.split('_');
    const [channelType, channelIndex] = channelDetails;
    updatedChannelKey.setChannelType(channelType);
    updatedChannelKey.setChannelIndex(channelIndex);
    updateChannelKey(updatedChannelKey);
  };

  const getMaxComponentSensorLabel = (channelGroup) => {
    // display user created label for all but BLE/Device sensors
    if (Number(channelGroup.channelGroupTypeId) === ChannelGroupType.BLUETOOTH_LE.id) {
      return 'Device';
    }
    return channelGroup.label;
  };

  useEffect(() => {
    const isRamax = isRamaxDevice(channelKey.macAddress);
    setIsMaxDevice(isRamax);
  }, []);

  return (
    <>
      <FormErrorHandler errorList={errorList}>
        <ConditionalRender show={deviceList.length > 0}>
          <div className="form-group row">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="col-form-label col-md-3 text-md-right">Device</label>
            <div className="col-auto">
              <AltErrorWrapper fieldId="channelKey">
                <select
                  className="form-control"
                  onChange={handleDeviceAssignmentChange}
                  value={channelKey.macAddress}
                  defaultValue="XX:XX:XX:XX:XX:XX"
                >
                  <option value="XX:XX:XX:XX:XX:XX" disabled>-- Select a Device --</option>
                  {deviceList
                    .filter((i) => !i.isSuspended)
                    .sort((a, b) => a.displayName.localeCompare(b.displayName))
                    .map((device) => (
                      <option
                        key={device.uuid}
                        value={device.uuid}
                      >
                        {device.displayName}
                      </option>
                    ))}
                </select>
              </AltErrorWrapper>
            </div>
          </div>
        </ConditionalRender>

        {
          loadingDeviceData ? (
            <ProgressSpinner />
          ) : (
            <>
              <ConditionalRender show={isMaxDevice}>
                <div className="form-group row">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="col-form-label col-md-3 text-md-right">Component</label>
                  <div className="col-auto">
                    {/* todo: validation/err handling ? */}
                    <select
                      className="form-control always-inline"
                      onChange={handleMaxComponentChange}
                      value={channelKey.channelGroupMacAddress || ''}
                    >
                      <option selected disabled value="">-- Select a Component --</option>
                      <option
                        key={channelKey.macAddress}
                        value={channelKey.macAddress}
                      >
                        Base Station Component
                      </option>
                      {maxComponentList.filter((cx) => cx.channelGroupMacAddress !== channelKey.macAddress).map((component) => (
                        <option
                          key={component.channelGroupMacAddress}
                          value={component.channelGroupMacAddress}
                        >
                          {component.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* RAMAX Sensor selector */}
                {
                    channelKey.channelGroupMacAddress && (
                    <div className="form-group row">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="col-form-label col-md-3 text-md-right">Sensor</label>
                      <div className="col-auto">
                        <AltErrorWrapper fieldId="channelKey">
                          <select
                            className="form-control always-inline"
                            onChange={handleMaxSensorChange}
                            value={`${channelKey.channelGroupType}_${channelKey.channelGroupIndex}`}
                          >
                            <option selected disabled value="0_0">-- Select a Sensor --</option>
                            {getMaxComponentSensorList()
                              .filter((channelGroup) => channelGroup.channelGroupTypeId !== 0)
                              .map((group) => (
                                <option
                                  key={`${group.channelGroupTypeId}_${group.channelGroupIndex}`}
                                  value={`${group.channelGroupTypeId}_${group.channelGroupIndex}`}
                                >
                                  {getMaxComponentSensorLabel(group)}
                                </option>
                              ))}
                          </select>
                        </AltErrorWrapper>
                      </div>
                    </div>
                    )
                }
              </ConditionalRender>

              <ConditionalRender show={channelGroupList.length > 0 && !isMaxDevice}>
                <div className="form-group row">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="col-form-label col-md-3 text-md-right">Sensor</label>
                  <div className="col-auto">
                    <AltErrorWrapper fieldId="channelKey">
                      <select
                        className="form-control always-inline"
                        onChange={handleChannelGroupChange}
                        value={`${channelKey.channelGroupIdentifier}`}
                      >
                        <option selected disabled value="_0_0">-- Select a Sensor --</option>
                        {channelGroupList
                          .filter((channelGroup) => channelGroup.channelGroupTypeId !== 0)
                          .map((group) => (
                            <option
                              key={`${group.channelGroupMacAddress}_${group.channelGroupTypeId}_${group.channelGroupIndex}`}
                              value={`${group.channelGroupMacAddress}_${group.channelGroupTypeId}_${group.channelGroupIndex}`}
                            >
                              {group.label}
                            </option>
                          ))}
                      </select>
                    </AltErrorWrapper>
                  </div>
                </div>
              </ConditionalRender>

              <ConditionalRender show={channelList.length > 0}>
                <div className="form-group row">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="col-form-label col-md-3 text-md-right">Channel</label>
                  <div className="col-auto">
                    <AltErrorWrapper fieldId="channelKey">
                      <select
                        className="form-control"
                        value={channelKey.channelIdentifier}
                        onChange={handleChannelChange}
                      >
                        <option selected disabled value="0_0">-- Select a Channel --</option>
                        <ConditionalRender
                          show={(channelKey.channelGroupType === ChannelGroupType.TEMP_ANALOG.id
                        || channelKey.channelGroupType === ChannelGroupType.INTERNAL_ANALOG.id)}
                        >
                          <ConditionalRender show={!analogComputedSensor}>
                            {channelList
                              .filter((channel) => !(channel instanceof AnalogComputedChannel))
                              .map((channel) => (
                                <option
                                  key={`${channel.channelType.id}_${channel.index}`}
                                  value={`${channel.channelType.id}_${channel.index}`}
                                >
                                  {channel.displayName}
                                </option>
                              ))}
                          </ConditionalRender>
                          <ConditionalRender
                            show={(analogComputedSensor !== undefined && analogComputedSensor !== null)}
                          >
                            {channelList.map((channel) => (
                              <option
                                key={`${channel.channelType.id}_${channel.index}`}
                                value={`${channel.channelType.id}_${channel.index}`}
                              >
                                {channel.displayName}
                              </option>
                            ))}
                          </ConditionalRender>
                        </ConditionalRender>

                        <ConditionalRender
                          show={!(channelKey.channelGroupType === ChannelGroupType.TEMP_ANALOG.id
                        || channelKey.channelGroupType === ChannelGroupType.INTERNAL_ANALOG.id)}
                        >
                          {channelList.map((channel) => (
                            <option
                              key={`${channel.channelType.id}_${channel.index}`}
                              value={`${channel.channelType.id}_${channel.index}`}
                            >
                              {channel.displayName}
                            </option>
                          ))}
                        </ConditionalRender>
                      </select>
                    </AltErrorWrapper>
                  </div>
                </div>
              </ConditionalRender>
            </>
          )
        }

      </FormErrorHandler>
    </>
  );
}

ChannelSelector.defaultProps = {
  isQuickAlert: false,
};

ChannelSelector.propTypes = {
  channelKey: PropTypes.instanceOf(ChannelKey).isRequired,
  updateChannelKey: PropTypes.func.isRequired,
  setIsDeviceSensor: PropTypes.func.isRequired,
  errorList: PropTypes.arrayOf(PropTypes.shape({
    field: PropTypes.string,
    message: PropTypes.string,
  })).isRequired,
  isQuickAlert: PropTypes.bool,
};

export default ChannelSelector;
